/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.PAGE.MASTER — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/PageMasterPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail) over the SAME
 * data/logic layer only:
 *   - reads via apiDb (csm_page_master + csm_page_master_tl), resolving the page
 *     name AND the parent FK to their translated display text against the ACTIVE
 *     locale (§8, §17.B4 — never a raw id/code reaches render),
 *   - the activate/deactivate write routes through the Common Routine
 *     (routedUpdate) exactly like the desktop page — §2 single write path.
 *
 * The desktop screen owns the hierarchy-shaping tools (drag reparent, promote /
 * demote, sort order, translation editing, create / delete). Those stay on the
 * desktop; the phone surfaces the registry for review plus the one scalar a
 * touch UI handles cleanly — the Active flag. §16: never the desktop grid
 * re-hosted in a phone viewport.
 *
 * A mobile-native extra: each page is tagged "Mobile ready" or "Desktop only" by
 * checking whether it has a registered mobile component — derived from the mobile
 * page registry, NOT a DB column (the desktop schema has none), so the pill can
 * never drift from what the phone can actually open.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Power, FileText, Layers, Smartphone, Monitor } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobileRelatedChildren, type MobileChildGroup, type MobileChildItem } from '../components/MobileRelatedChildren';
import { buildMobileTranslationsGroup } from '../components/mobile_translations_group';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { MOBILE_PAGE_COMPONENTS } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.PAGE.MASTER';

interface MobilePageRow {
  page_id: number;
  internal_code: string;
  route_path: string;
  parent_page_id: number | null;
  is_active: 'Y' | 'N';
  is_user_managed: 'Y' | 'N';
  requires_signoff: 'Y' | 'N';
  display_text: string;
  parent_label: string;
  /** Every locale's text, kept so the related section can list them (§17.B14). */
  tl: TlRow[];
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { locale_code: string; display_text: string }

type RawPageRow = {
  page_id: number;
  internal_code: string;
  route_path: string | null;
  parent_page_id: number | null;
  is_active: 'Y' | 'N';
  is_user_managed: 'Y' | 'N';
  requires_signoff: 'Y' | 'N';
  csm_page_master_tl?: TlRow[];
};

// A page with an empty route is a navigation group node (NAV.*), not a leaf screen.
function isGroupNode(row: MobilePageRow): boolean {
  return row.route_path.trim() === '';
}

export function MobilePageMasterPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [pages, setPages] = useState<MobilePageRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [desktopOnly, setDesktopOnly] = useState(false);
  const [selected, setSelected] = useState<MobilePageRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Related children (lazy-loaded when the detail sheet opens).
  const [childGroups, setChildGroups] = useState<MobileChildGroup[]>([]);
  const [childBusy, setChildBusy] = useState(false);

  // Which page codes the mobile app can actually open (§16 — the mobile registry
  // is the single source of truth for mobile-native screens). Stable for the
  // lifetime of the module, so an empty dep array is correct.
  const mobileCodes = useMemo(() => new Set(Object.keys(MOBILE_PAGE_COMPONENTS)), []);
  const isMobileReady = (code: string) => mobileCodes.has(code);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await fetchAllRows((from, to) => apiDb
        .from('csm_page_master')
        .select(`
          page_id, internal_code, route_path, parent_page_id,
          is_active, is_user_managed, requires_signoff,
          csm_page_master_tl ( locale_code, display_text )
        `)
        .order('internal_code').order('page_id').range(from, to));

      const raw = (data ?? []) as RawPageRow[];

      // Resolve each page's name against the active locale first, so a parent FK
      // can be shown as its translated name rather than a raw id (§17.B4).
      const labelOf = (r: RawPageRow): string => {
        const tls = r.csm_page_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        return exact?.display_text ?? fb?.display_text ?? r.internal_code;
      };
      const labels: Record<number, string> = {};
      for (const r of raw) labels[r.page_id] = labelOf(r);

      const rows: MobilePageRow[] = raw.map((r) => ({
        page_id: r.page_id,
        internal_code: r.internal_code,
        route_path: r.route_path ?? '',
        parent_page_id: r.parent_page_id,
        is_active: r.is_active,
        is_user_managed: r.is_user_managed,
        requires_signoff: r.requires_signoff,
        display_text: labels[r.page_id],
        parent_label: r.parent_page_id != null ? (labels[r.parent_page_id] ?? '') : '',
        tl: r.csm_page_master_tl ?? [],
      }));
      setPages(rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (page labels follow it, §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Lazy-load the selected page's children when the detail sheet opens: the roles
  // that grant access (V/A/C/D/S), its sub-pages, and — transitively via those
  // roles — the job descriptions that reach it. Names resolved via _tl against the
  // active locale.
  useEffect(() => {
    if (!selected) { setChildGroups([]); setChildBusy(false); return; }
    const pageId = selected.page_id;
    let cancelled = false;
    setChildBusy(true);
    void (async () => {
      const one = <T,>(v: T | T[] | undefined): T | undefined => (Array.isArray(v) ? v[0] : v);
      const pickTl = (tls: TlRow[] | undefined, fb: string) => {
        const list = tls ?? [];
        return list.find((x) => x.locale_code === activeLocale)?.display_text
          ?? list.find((x) => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? fb;
      };
      const byLabel = (a: MobileChildItem, b: MobileChildItem) => a.label.localeCompare(b.label);
      try {
        const [rpa, subs] = await Promise.all([
          apiDb.from('csm_role_page_access')
            .select('role_id, can_view, can_add, can_change, can_delete, can_signoff, csm_role_master ( internal_code, csm_role_master_tl ( locale_code, display_text ) )')
            .eq('page_id', pageId),
          apiDb.from('csm_page_master')
            .select('page_id, internal_code, csm_page_master_tl ( locale_code, display_text )')
            .eq('parent_page_id', pageId),
        ]);

        type RoleEmbed = { internal_code: string; csm_role_master_tl?: TlRow[] };
        type RpaRow = { role_id: number; can_view: boolean; can_add: boolean; can_change: boolean; can_delete: boolean; can_signoff: boolean; csm_role_master?: RoleEmbed | RoleEmbed[] };
        const rpaRows = (rpa.data ?? []) as RpaRow[];
        const roles: MobileChildItem[] = rpaRows.map((r) => {
          const role = one(r.csm_role_master);
          const badges: string[] = [];
          if (r.can_view) badges.push('V');
          if (r.can_add) badges.push('A');
          if (r.can_change) badges.push('C');
          if (r.can_delete) badges.push('D');
          if (r.can_signoff) badges.push('S');
          return { id: r.role_id, label: pickTl(role?.csm_role_master_tl, role?.internal_code ?? `#${r.role_id}`), sub: role?.internal_code, badges };
        }).sort(byLabel);

        type SubEmbed = { page_id: number; internal_code: string; csm_page_master_tl?: TlRow[] };
        const subpages: MobileChildItem[] = ((subs.data ?? []) as SubEmbed[]).map((r) => ({
          id: r.page_id, label: pickTl(r.csm_page_master_tl, r.internal_code), sub: r.internal_code,
        })).sort(byLabel);

        // Transitive: job descriptions that include any role granting this page.
        const roleIds = Array.from(new Set(rpaRows.map((r) => r.role_id)));
        let jobs: MobileChildItem[] = [];
        if (roleIds.length > 0) {
          const { data: jdrData } = await apiDb.from('csm_job_description_role')
            .select('job_description_id, csm_job_description ( internal_code, csm_job_description_tl ( locale_code, display_text ) )')
            .in('role_id', roleIds);
          type JobEmbed = { internal_code: string; csm_job_description_tl?: TlRow[] };
          type JdrRow = { job_description_id: number; csm_job_description?: JobEmbed | JobEmbed[] };
          const seen = new Set<number>();
          jobs = ((jdrData ?? []) as JdrRow[])
            .filter((r) => { if (seen.has(r.job_description_id)) return false; seen.add(r.job_description_id); return true; })
            .map((r) => { const j = one(r.csm_job_description); return { id: r.job_description_id, label: pickTl(j?.csm_job_description_tl, j?.internal_code ?? `#${r.job_description_id}`), sub: j?.internal_code }; })
            .sort(byLabel);
        }

        if (cancelled) return;
        setChildGroups([
          { key: 'roles', table: 'csm_role_master', pkColumn: 'role_id', title: t('mobile.pages.related.roles', 'Roles with access'), items: roles },
          { key: 'subpages', table: 'csm_page_master', pkColumn: 'page_id', title: t('mobile.pages.related.subpages', 'Sub-pages'), items: subpages },
          { key: 'jobs', table: 'csm_job_description', pkColumn: 'job_description_id', title: t('mobile.pages.related.job_descriptions', 'Job Descriptions'), items: jobs },
          buildMobileTranslationsGroup(selected.tl, t, activeLocale),
        ]);
      } catch {
        if (!cancelled) setChildGroups([]);
      } finally {
        if (!cancelled) setChildBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return pages.filter((p) => {
      if (statusFacet === 'active' && p.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && p.is_active !== 'N') return false;
      if (desktopOnly && mobileCodes.has(p.internal_code)) return false;
      if (!q) return true;
      return (
        p.display_text.toLowerCase().includes(q) ||
        p.internal_code.toLowerCase().includes(q) ||
        p.route_path.toLowerCase().includes(q) ||
        p.parent_label.toLowerCase().includes(q)
      );
    });
  }, [pages, filter, statusFacet, desktopOnly, mobileCodes]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.pages.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const sourceLabel = (p: MobilePageRow) =>
    p.is_user_managed === 'Y' ? t('mobile.pages.source_user', 'User') : t('mobile.pages.source_code', 'Registry');

  const subLine = (p: MobilePageRow): string => {
    const route = p.route_path || t('mobile.pages.no_route', 'Group');
    return p.parent_label ? `${route} · ${p.parent_label}` : route;
  };

  const toggleActive = async (p: MobilePageRow) => {
    setActionBusy(true);
    setError(null);
    const next = p.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'csm_page_master', 'page_id', p.page_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.pages.title', 'Pages')}
        description={t('mobile.pages.description', 'The navigable page registry, its hierarchy and mobile availability.')}
      />

      <MobileStatStrip
        stats={[
          { value: pages.length, label: t('mobile.pages.stat.total', 'Total') },
          { value: pages.filter((p) => p.is_active === 'Y').length, label: t('common.boolean.active', 'Active') },
          { value: pages.filter((p) => mobileCodes.has(p.internal_code)).length, label: t('mobile.pages.stat.mobile', 'Mobile') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.pages.search', 'Search pages')}
          aria-label={t('mobile.pages.search', 'Search pages')}
        />
      </div>

      <MobileFilterChips
        chips={[
          ...statusFacets.map((f) => ({ value: f.key, label: f.label })),
          { value: '__desktop', label: t('mobile.pages.desktop_only', 'Desktop only') },
        ]}
        isActive={(v) => (v === '__desktop' ? desktopOnly : statusFacet === v)}
        onSelect={(v) => (v === '__desktop' ? setDesktopOnly((p) => !p) : setStatusFacet(v as StatusFacet))}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.pages.none', 'No pages match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((p) => {
            const group = isGroupNode(p);
            return (
              <MobileCardRow
                key={p.page_id}
                lead={group ? <Layers /> : <FileText />}
                leadTone={group ? 'slate' : 'blue'}
                title={p.display_text}
                subtitle={`${p.internal_code} · ${subLine(p)}`}
                trailing={
                  <MobileStatusPill tone={p.is_active === 'Y' ? 'ok' : 'no'}>
                    {p.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
                chevron
                onClick={() => setSelected(p)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.pages.detail', 'Page detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{isGroupNode(selected) ? <Layers size={20} /> : <FileText size={20} />}</span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.pages.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow
                label={t('mobile.pages.availability', 'Availability')}
                value={
                  isMobileReady(selected.internal_code)
                    ? <MobileStatusPill tone="ok"><Smartphone size={12} /> {t('mobile.pages.mobile_ready', 'Mobile ready')}</MobileStatusPill>
                    : <MobileStatusPill tone="info"><Monitor size={12} /> {t('mobile.pages.desktop_only', 'Desktop only')}</MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.pages.route', 'Route')} value={selected.route_path || t('mobile.pages.no_route', 'Group')} />
              <MobileDetailRow label={t('mobile.pages.parent', 'Parent')} value={selected.parent_label || t('mobile.pages.parent_root', 'Top level')} />
              <MobileDetailRow label={t('mobile.pages.source', 'Source')} value={sourceLabel(selected)} />
              <MobileDetailRow
                label={t('mobile.pages.requires_signoff', 'Requires sign-off')}
                value={
                  <MobileStatusPill tone={selected.requires_signoff === 'Y' ? 'wait' : 'no'}>
                    {selected.requires_signoff === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}
                  </MobileStatusPill>
                }
              />
            </MobileContentCard>
            <MobileRelatedChildren groups={childGroups} busy={childBusy} session={session} />
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
